// i18n: translated
/**
 * アプリケーションレイアウト
 *
 * Sidebar + Header + メインコンテンツエリアの3ペイン構成。
 * 認証ガード: 未認証ユーザーは /login にリダイレクト。
 * SPEC 7.1 ページ一覧準拠。
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useT } from '@/i18n';
import { shellNs } from '@/i18n/namespaces/shell';
import { safeAiReturnTo } from '@/utils/aiConnection';
import { useAuth } from '@/hooks/useAuth';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import Sidebar from '@/components/layout/Sidebar';
import Header from '@/components/layout/Header';
import ToastContainer from '@/components/common/ToastContainer';
import NotificationPopupListener from '@/components/layout/NotificationPopupListener';
import AnnouncementBanner from '@/components/layout/AnnouncementBanner';
import AdminLocaleProvider from '@/components/layout/AdminLocaleProvider';

/**
 * 管理画面の表示サイズ (高橋 2026-09-14): html に付けると `index.css` の `html.ac-admin-compact` が
 * font-size を 99% (= 従来 110% の 0.9 倍) にする。rem 基準の文字・余白・アイコンがタスク管理の
 * 90% 表示と同じ大きさになる。依頼者ポータル / ログイン画面には付けない (この shell の mount 中だけ)。
 */
export const ADMIN_COMPACT_HTML_CLASS = 'ac-admin-compact';

export default function AppLayout() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // 管理画面 shell の mount 中だけ html に class を付ける (portal で描画されるメニューも同じ倍率になる)
  useEffect(() => {
    document.documentElement.classList.add(ADMIN_COMPACT_HTML_CLASS);
    return () => {
      document.documentElement.classList.remove(ADMIN_COMPACT_HTML_CLASS);
    };
  }, []);

  // 認証状態の読み込み中
  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  // 未認証 → ログインページへリダイレクト
  if (!user) {
    const returnTo = safeAiReturnTo(`${location.pathname}${location.search}`);
    return (
      <Navigate
        to={returnTo === '/' ? '/login' : `/login?returnTo=${encodeURIComponent(returnTo)}`}
        replace
      />
    );
  }

  return (
    // 表示言語 (日本語 / English、admin-english-toggle-20260924 / ADR-081)。認証済み shell の
    // 中だけを包む — 依頼者ポータル・ログイン画面は Provider の外なので常に日本語。
    <AdminLocaleProvider userId={user.userId}>
      <AppShell
        sidebarCollapsed={sidebarCollapsed}
        onToggleSidebar={() => setSidebarCollapsed((prev) => !prev)}
      />
    </AdminLocaleProvider>
  );
}

interface AppShellProps {
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
}

/** 認証済みの shell 本体 (ヘッダー / サイドバー / コンテンツ / トースト)。 */
function AppShell({ sidebarCollapsed, onToggleSidebar }: AppShellProps) {
  return (
    <div className="flex h-screen flex-col overflow-hidden bg-white text-gray-800 dark:bg-gray-900 dark:text-gray-300">
      {/* ヘッダー (全幅) */}
      <Header onSidebarToggle={onToggleSidebar} />

      {/* サイドバー + コンテンツ */}
      <div className="flex flex-1 overflow-hidden">
        {/* サイドバー */}
        <Sidebar collapsed={sidebarCollapsed} />

        {/* コンテンツ */}
        <main className="relative flex-1 overflow-y-auto" data-app-scroll-owner="">
          {/* 高さ契約 shell (steering 524 Batch 3)。
              main は block 整形コンテキストのため、banner と Outlet を素の兄弟として
              積むと page-root の h-full (= height:100%) が banner を差し引かずに
              main の content box 高さへ解決され、banner の高さぶんちょうど overflow する。
              main 自体は flex 化せず (page scroll owner と既存 class 契約を保つ)、
              内側に flex column を 1 段設けて banner と route content を分ける。 */}
          <div className="flex h-full flex-col" data-testid="app-layout-shell">
            {/* 運営からのお知らせバナー (steering 488)。
                tenant admin surface かつ認証済みのときだけ描画される
                (portal / ops では component 側の 2 段ガードで非表示)。
                banner の高さは件数・折り返しで変わるため、定数で扱わず
                shrink-0 の flex item として実測高をそのまま占有させる。 */}
            <div className="shrink-0" data-testid="app-layout-banner-slot">
              <AnnouncementBanner />
            </div>
            {/* route content。min-h-0 で flex item 既定の min-height:auto を解除し、
                banner 差引後の残り高を確定させる。page-root の h-full はこの高さへ
                解決される (page 側の class は変更しない)。 */}
            <div className="min-h-0 flex-1" data-testid="app-layout-outlet">
              <LocalizedOutletBoundary>
                <Outlet />
              </LocalizedOutletBoundary>
            </div>
          </div>
        </main>
      </div>

      {/* トースト通知コンテナ */}
      <ToastContainer />

      {/* 新着通知ポップアップ + 通知音 (描画なし。新着をポーリングして ToastContainer に出す) */}
      <NotificationPopupListener />
    </div>
  );
}

/**
 * 画面 (Outlet) だけを包むエラー境界 (admin-english-toggle phase 2 / ADR-081)。
 * 表示言語の Provider の中なので、英語表示では英語の文言を出す。画面の中で render 例外が
 * 起きてもヘッダー・サイドバーは残り、別の画面へ移動すれば (path が変われば) 復帰する。
 * root の ErrorBoundary (`main.tsx`) は Provider の外で、shell 自体の例外を日本語のまま受ける。
 */
function LocalizedOutletBoundary({ children }: { children: ReactNode }) {
  const t = useT(shellNs);
  const location = useLocation();
  return (
    <ErrorBoundary
      inline
      resetKey={location.pathname}
      labels={{ message: t('errorBoundary.message'), reload: t('errorBoundary.reload') }}
    >
      {children}
    </ErrorBoundary>
  );
}
